<script setup lang="ts">
import { ref } from 'vue'
import type { Column } from '@tanstack/vue-table'
import { ChevronDown, Columns3 } from 'lucide-vue-next'
import { LIBRARY_COLUMN_NAMES } from '../consts/libraryColumnNames'
import type { ViewActiveMapped } from '../composables/useTableLibrary'
import type { Elemental } from '~~/src/types/elemental'
import type { ColumnList } from '~~/src/types/library'
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import SearchHeader from '~~/src/components/DrawerSidebarRight/components/SearchHeader/index.vue'
interface Props {
  allFeedData: Elemental[] | ColumnList[] | ViewActiveMapped
  separatedByType: boolean
  hideFilters?: boolean
  columns: Column<ColumnList, unknown>[]
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'update:separatedByType', value: boolean): void
  (e: 'update:filterByMe', value: boolean): void
  (e: 'update:savedMode', value: boolean): void
}>()

const separatedByType = ref(props.separatedByType)
const hideFilters = ref(props.hideFilters)

const toggleSeparatedByType = (e: boolean) => {
  emit('update:separatedByType', e)
}

const filterByMe = (e: boolean) => {
  emit('update:filterByMe', e)
}

const toggleSavedMode = (e: boolean) => {
  emit('update:savedMode', e)
}
</script>

<template>
  <TransitionSoftSlide direction="down">
    <div v-if="!hideFilters" class="wrapperFilter">
      <div class="flex gap-1 sm:gap-2 sm:justify-between items-center py-4 px-4 filters-container">
        <div class="flex gap-1 sm:gap-2 flex-row-reverse sm:flex-row items-center w-full max-w-[800px]">
          <!-- Filter list -->
          <LibraryFilterMenu
            :separated-by-type="separatedByType"
            @update:separated-by-type="toggleSeparatedByType"
            @update:filter-by-me="filterByMe"
            @update:saved-mode="toggleSavedMode"
          />
          <!-- Search -->
          <SearchHeader class="w-full !m-0" />
        </div>
        <!-- Columns -->
        <DropdownMenu>
          <DropdownMenuTrigger as-child>
            <div class="hide-columns">
              <Button variant="outline" class="ml-auto group relative hidden sm:flex">
                Columns <ChevronDown class="ml-2 h-4 w-4" />
              </Button>
              <Button
                variant="snack-ghost"
                size="icon"
                class="ml-auto group relative flex sm:hidden rounded-full shrink-0"
              >
                <Columns3 class="h-4 w-4" />
              </Button>
            </div>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="end">
            <DropdownMenuCheckboxItem
              v-for="column in props.columns.filter((column) => column.getCanHide())"
              :key="column.id"
              class="capitalize"
              :checked="column.getIsVisible()"
              @update:checked="value => column.toggleVisibility(!!value)"
            >
              {{ LIBRARY_COLUMN_NAMES[column.id] }}
            </DropdownMenuCheckboxItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  </TransitionSoftSlide>
</template>

<style lang="scss">
.hide-columns {
  @container wrapperFilter (max-width: 532px) {
    display: none;
  }
}
</style>
